import { css } from '@emotion/react';

/*
  Resource Form - My Profile
  Mostly ported from Dashboard ResourceForm
*/
import React from 'react';
import styled from '@emotion/styled';

import { Field, Form } from 'formik';

import { Theme } from 'ui_system/theme';
import RadioPair from 'ui_system/forms/RadioPair';
import TimezoneSelect from 'ui_system/select/timezone_select/TimezoneSelect';

import PhoneInput from 'views/anyguide/resources/components/PhoneInput';
import PlacesInput from 'views/anyguide/resources/components/PlacesInput';
import ImageUploader from 'views/anyguide/resources/components/ImageUploader';
import ErrorFocus from 'views/anyguide/resources/components/ErrorFocus';

import { Button } from 'ui_system/token_auth/elements/Button';
import { t } from 'services/translation';

import {
  FormContainer,
  FormSection,
  Label,
  FieldSection,
  Error,
  TextAreaField,
  SubLabel,
} from 'views/anyguide/resources/formStyles';
import { titleize } from 'lib/string_utils';

const PlainText = styled(Label)`
  font-weight: 400;
  &:first-letter {
    text-transform: none;
  }
`;

const PhoneInputField = styled.div`
  width: 100%;
  @media (min-width: ${Theme.mobile.tinyMobileBreakpointWidth}px) {
    width: 220px;
  }
`;

const SectionTitle = styled.h2`
  text-transform: uppercase;
`;

const HFlex = styled('div')`
  display: flex;
  align-items: ${(props) => (props.alignItems ? props.alignItems : 'center')};
  justify-content: ${(props) =>
    props.justifyContent ? props.justifyContent : 'space-between'};
`;

const Actions = styled(HFlex)`
  justify-content: flex-end;
  width: 100%;
`;

const SubmitButton = styled(Button)`
  width: 140px;
  font-size: 16px;
  padding-left: 30px;
  padding-right: 30px;
  margin-top: 40px;
`;

// Prop helpers ==========
const textAreaFieldProps = {
  css: TextAreaField,
  type: 'textarea',
  component: 'textarea',
};

const narrowField = css`
  width: 100%;
  @media (min-width: ${Theme.mobile.phoneWidth}px) {
    width: 50%;
  }
`;

const NarrowField = styled.div`
  ${narrowField};
`;

// ========== Prop helpers

// Horizontal line / divider
export const HR = styled('hr')`
  border: none;
  height: 1px;
  margin-bottom: 30px;
  background: ${Theme.colors.lightLine};
`;

const ChangePasswordLink = styled.button`
  color: ${(props) => (props.color ? props.color : props.theme.linkColor)};
  text-decoration: underline;
  font-size: 16px;
`;

const Link = styled.a`
  color: ${(props) => (props.color ? props.color : props.theme.linkColor)};
  &:hover {
    color: ${(props) => (props.color ? props.color : props.theme.linkColor)};
  }
`;

const renderLanguageSelector = ({ linkColor, setFieldValue }) => {
  return (
    <div>
      <SectionTitle>{t('LANGUAGE_PREFERENCE')}</SectionTitle>
      <Label>{t('PREFERRED_LANGUAGE_FOR_COMMUNICATION')}</Label>
      <SubLabel>
        {t(
          'THIS_WILL_BE_THE_LANGUAGE_USED_ON_YOUR_PORTAL_AND_ANY_COMMUNICATIONS_EMAIL_AND_TEXT_SENT_TO_YOU'
        )}
      </SubLabel>

      <FieldSection>
        <Field
          name="i18n"
          component={RadioPair}
          values={['en-US', 'fr-CA']}
          labels={[t('ENGLISH'), t('FRENCH')]}
          handleToggle={(name, value) => setFieldValue(name, value)}
          format="minimal"
          color={linkColor}
        />
      </FieldSection>

      <HR />
    </div>
  );
};

// Form content
const ResourceForm = ({
  t,
  values,
  errors,
  dirty,
  setFieldValue,
  isSubmitting,
  onChangePasswordClick,
  showLanguageSelect,
  isOnlineExperiencesAllowed,
  // Optional
  linkColor,
  isPhoneRequired,
  isBioRequired,
  isPhotoRequired,
}) => {
  return (
    <Form>
      <ErrorFocus />
      <FormContainer>
        <FormSection>
          <SectionTitle>{t('YOUR INFORMATION')}</SectionTitle>
          <Label>{t('NAME')}</Label>
          <FieldSection>
            <PlainText>
              {values.firstName} {values.lastName}
            </PlainText>
          </FieldSection>

          <Label>{t('EMAIL')}</Label>
          <FieldSection>
            <PlainText>{values.email}</PlainText>
          </FieldSection>

          <Label>
            {`${titleize(t('PHONE_NUMBER'))}`}{' '}
            {isPhoneRequired ? null : `(${t('OPTIONAL')})`}
          </Label>
          <SubLabel>{t('PREFERABLY_MOBILE')}</SubLabel>
          <FieldSection>
            <Field
              name="phone"
              phone={values.phone}
              render={(field) => (
                <PhoneInputField>
                  <PhoneInput {...field} phone={values.phone} />
                </PhoneInputField>
              )}
            />
            {errors.phone && <Error>{errors.phone}</Error>}
          </FieldSection>

          <Label>{t('ADDRESS')}</Label>
          <FieldSection>
            <Field
              name="location"
              handleAddressChanged={(address) =>
                setFieldValue('address', address)
              }
              handleGeocodeSelected={({ address, placesAddress, placeId }) => {
                setFieldValue('address', address);
                setFieldValue('placesAddress', placesAddress);
                setFieldValue('placeId', placeId);
              }}
              component={PlacesInput}
              address={values.address}
              placeholder={t('ENTER_YOUR_ADDRESS')}
              i18n={values.i18n}
            />
            {errors.location && <Error>{errors.location}</Error>}
          </FieldSection>

          <HR />

          <FieldSection>
            <ChangePasswordLink
              type="button"
              color={linkColor}
              onClick={onChangePasswordClick}
            >
              {t('CHANGE_PASSWORD')}
            </ChangePasswordLink>
          </FieldSection>

          <HR />

          {showLanguageSelect
            ? renderLanguageSelector({ linkColor, setFieldValue })
            : ''}

          <SectionTitle>{t('PUBLIC_PROFILE')}</SectionTitle>
          {isBioRequired && (
            <React.Fragment>
              <Label>{`${t('SHORT_BIO')}`}</Label>
              <SubLabel>
                {t(
                  'THIS_WILL_BE_DISPLAYED_TO_PARTICIPANTS_WHEN_THEY_BOOK_YOUR',
                  '',
                  {
                    experienceNameSingular: t(
                      'EXPERIENCE_NAME_SINGULAR'
                    ).toLowerCase(),
                  }
                )}
              </SubLabel>
              <FieldSection>
                <Field
                  {...textAreaFieldProps}
                  name="bio"
                  placeholder={t('1_OR_2_SHORT_PARAGRAPHS')}
                />
                {errors.bio && <Error>{errors.bio}</Error>}
              </FieldSection>
            </React.Fragment>
          )}
          <Label>
            {`${t('PROFILE_PICTURE')}`}{' '}
            {isPhotoRequired ? null : `(${t('OPTIONAL')})`}
          </Label>
          <SubLabel>
            {t('UPLOAD_A_PHOTO_THAT_CLEARLY_SHOWS_YOUR_FACE')}
          </SubLabel>
          <FieldSection>
            <Field
              name="avatar"
              component={ImageUploader}
              imageUrl={values.avatarUrl}
              color={linkColor}
              css={css`
                height: 100%;
              `}
            />
            {errors.avatar && <Error>{errors.avatar}</Error>}
          </FieldSection>

          {isOnlineExperiencesAllowed && (
            <React.Fragment>
              <HR />
              <SectionTitle>{t('ONLINE_CLASS_PREFERENCE')}</SectionTitle>
              <SubLabel>
                We encourage you to check out our{' '}
                <Link
                  href="https://michaels-instructors.helpscoutdocs.com/article/592-equipment-tips-for-successful-online-classes"
                  target="_blank"
                  color={linkColor}
                >
                  guidelines for hosting a successful live online experience
                </Link>
                .
              </SubLabel>

              <Label>Default Timezone</Label>
              <SubLabel>
                This is the timezone in which you will see your online class
                schedules.
              </SubLabel>
              <FieldSection>
                <Field
                  name="timeZone"
                  render={({ form }) => (
                    <NarrowField>
                      <TimezoneSelect
                        value={values.timeZone}
                        onChange={({ value }) =>
                          form.setFieldValue('timeZone', value)
                        }
                      />
                      {errors.timeZone && <Error>{errors.timeZone}</Error>}
                    </NarrowField>
                  )}
                />
              </FieldSection>
            </React.Fragment>
          )}
        </FormSection>

        <Actions>
          <SubmitButton
            type="submit"
            disabled={isSubmitting}
            faded={!dirty || values.avatarProgress !== 100}
            text={t('SAVE')}
          />
        </Actions>
      </FormContainer>
    </Form>
  );
};

export default ResourceForm;
